import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import {
  Alert,
  Badge,
  Card,
  EmptyState,
  PageHeader,
  SkeletonRows,
  mutedTextClass,
} from '@/components/layout/ui'
import type { BadgeTone } from '@/components/layout/ui'
import { cn } from '@/lib/cn'
import { messageFor } from '@/lib/errors'
import { formatDateTime } from '@/lib/format'
import { queryKeys } from '@/lib/queryClient'
import { adminApi } from './api'
import { Pager, TableScroll, Td, Th } from './parts'

/**
 * `/admin/audit` — who did what, to whom, and why.
 *
 * An audit trail nobody can read is a log file with extra steps. Every mutating
 * action in this console writes a row here inside the same transaction as the
 * change itself, so the trail cannot disagree with what actually happened, and
 * this screen is what makes that worth having.
 *
 * The rows are append-only: nothing in the product updates or deletes one.
 */
const ACTION_LABEL: Record<string, { label: string; tone: BadgeTone }> = {
  'grant.issued': { label: 'Plan granted', tone: 'warning' },
  'grant.revoked': { label: 'Grant revoked', tone: 'danger' },
  'user.admin_granted': { label: 'Admin granted', tone: 'warning' },
  'user.admin_revoked': { label: 'Admin removed', tone: 'danger' },
}

export default function AdminAuditPage() {
  const [page, setPage] = useState(1)

  const audit = useQuery({
    queryKey: queryKeys.adminAudit(page),
    queryFn: ({ signal }) => adminApi.audit({ page }, { signal }),
    placeholderData: (previous) => previous,
  })

  const rows = audit.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every privileged action taken in this console, with the person who took it and the reason they gave."
      />

      {audit.isError ? (
        <Alert tone="danger">{messageFor(audit.error)}</Alert>
      ) : (
        <Card>
          {audit.isPending ? (
            <div className="p-4">
              <SkeletonRows rows={6} />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing recorded yet"
              description="Granting a plan, revoking one, or changing someone's platform admin access will appear here."
            />
          ) : (
            <TableScroll>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Action</Th>
                  <Th>Target</Th>
                  <Th>Reason</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((entry) => {
                  const action = ACTION_LABEL[entry.action]
                  return (
                    <tr key={entry.id} className="transition hover:bg-ink-50">
                      <Td>
                        <span className="whitespace-nowrap tabular-nums">
                          {formatDateTime(entry.created_at)}
                        </span>
                      </Td>
                      <Td>
                        <span className="block">{entry.actor.name || '—'}</span>
                        <span className={cn('block text-xs', mutedTextClass)}>
                          {entry.actor.email}
                        </span>
                      </Td>
                      <Td>
                        <Badge tone={action?.tone ?? 'neutral'}>{action?.label ?? entry.action}</Badge>
                      </Td>
                      <Td>
                        <span className="block">{entry.target_label || entry.target_id}</span>
                        <span className={cn('block text-xs', mutedTextClass)}>
                          {entry.target_type}
                          {entry.detail?.plan ? ` · ${String(entry.detail.plan)}` : ''}
                        </span>
                      </Td>
                      <Td className="max-w-xs">
                        <span className="block break-words">{entry.reason || '—'}</span>
                      </Td>
                    </tr>
                  )
                })}
              </tbody>
            </TableScroll>
          )}

          {audit.data ? (
            <Pager
              page={audit.data.page}
              perPage={audit.data.per_page}
              total={audit.data.total}
              onPage={setPage}
              noun="entries"
            />
          ) : null}
        </Card>
      )}
    </>
  )
}
